// Deal Pipeline spec §4.1 / §4.3 — typed API client for /api/deals/* + workflow
// surface. Consumes the controllers in
// src/PhoenixCapital.Api/Controllers/Deals/{DealsController,DealWorkflowControllers}.cs.
//
// Conventions follow the Counterparties client: every namespaced
// object exports list/get/create/update plus the action-specific endpoints
// the spec exposes. Mutation hooks live in @/hooks/deals; this module is
// the raw transport.

import { api } from "./client";
import type { PagedResult } from "@/types/api";
import type { DealIntakeBundleDto, DealSubmissionAnswersDto } from "@/types/intakeProfile";
import type {
  AddCustomChecklistItemRequest,
  AdvanceStageRequest,
  AdvanceSubStateRequest,
  ArchiveRequest,
  AttestDocumentRequestRequest,
  AssignB1Request,
  BrokerFeeAuthorizationDto,
  BuyerConsultingFeeAuthorizationDto,
  ChangeCounterpartyRequest,
  CloneRequest,
  CreateDealRequest,
  CreateDocumentRequestRequest,
  DealAssignmentHistoryDto,
  DealDetailDto,
  DealEditBundleDto,
  DealListItemDto,
  DealListQuery,
  DealDeclineStateDto,
  DealStageTransitionDto,
  ConfirmDeclineRequest,
  DeclineRequest,
  ProposeDeclineRequest,
  IntakeChecklistDto,
  IntakeChecklistItemDto,
  PatchDealRequest,
  PreApprovalDocumentRequestDto,
  ReassignRequest,
  UpdateBrokerFeeAuthorizationRequest,
  UpdateBuyerConsultingFeeAuthorizationRequest,
  UpdateDealEditBundleRequest,
  UpdateDealRequest,
  UpsertChecklistItemRequest,
  WithdrawRequest,
} from "@/types/deals";

// ---------------------------------------------------------------------------
// Deal CRUD + lifecycle
// ---------------------------------------------------------------------------

export const dealsApi = {
  list: (query?: DealListQuery) =>
    api.get<PagedResult<DealListItemDto>>("/deals", query),

  get: (id: string) => api.get<DealDetailDto>(`/deals/${id}`),

  // Deal Submission spec §6 — qualitative intake profile +
  // multi-year financial history landed by the import engine.
  intakeBundle: (id: string) =>
    api.get<DealIntakeBundleDto>(`/deals/${id}/intake-bundle`),

  submissionAnswers: (id: string) =>
    api.get<DealSubmissionAnswersDto>(`/deals/${id}/submission-answers`),

  editBundle: (id: string) =>
    api.get<DealEditBundleDto>(`/deals/${id}/edit-bundle`),

  updateEditBundle: (id: string, body: UpdateDealEditBundleRequest) =>
    api.put<DealEditBundleDto>(`/deals/${id}/edit-bundle`, body),

  create: (body: CreateDealRequest) => api.post<DealDetailDto>("/deals", body),

  update: (id: string, body: UpdateDealRequest) =>
    api.put<DealDetailDto>(`/deals/${id}`, body),

  // Partial update — merges only the supplied fields (PUT above is a
  // full replacement that zeroes anything omitted).
  patch: (id: string, body: PatchDealRequest) =>
    api.patch<DealDetailDto>(`/deals/${id}`, body),

  remove: (id: string) => api.delete<void>(`/deals/${id}`),

  // Lifecycle
  advanceStage: (id: string, body: AdvanceStageRequest) =>
    api.patch<DealDetailDto>(`/deals/${id}/stage`, body),

  advanceSubState: (id: string, body: AdvanceSubStateRequest) =>
    api.patch<DealDetailDto>(`/deals/${id}/sub-state`, body),

  decline: (id: string, body: DeclineRequest) =>
    api.post<DealDetailDto>(`/deals/${id}/decline`, body),

  withdraw: (id: string, body: WithdrawRequest) =>
    api.post<DealDetailDto>(`/deals/${id}/withdraw`, body),

  // Deal Decline Workflow. The
  // deal-level decline is a separate mechanism from the
  // Stage=Declined decline above; it tracks DeclineStatus, and its confirm
  // also moves the deal to stage Declined (Q8, 2026-10-12).
  declineState: (id: string) =>
    api.get<DealDeclineStateDto>(`/deal-flow/deals/${id}/decline`),

  proposeDecline: (id: string, body: ProposeDeclineRequest) =>
    api.post<DealDeclineStateDto>(`/deal-flow/deals/${id}/decline/propose`, body),

  withdrawDeclineProposal: (id: string) =>
    api.post<DealDeclineStateDto>(`/deal-flow/deals/${id}/decline/withdraw`),

  confirmDecline: (id: string, body: ConfirmDeclineRequest) =>
    api.post<DealDeclineStateDto>(`/deal-flow/deals/${id}/decline/confirm`, body),

  archive: (id: string, body: ArchiveRequest = {}) =>
    api.post<DealDetailDto>(`/deals/${id}/archive`, body),

  restore: (id: string) => api.post<DealDetailDto>(`/deals/${id}/restore`),

  clone: (id: string, body: CloneRequest = {}) =>
    api.post<DealDetailDto>(`/deals/${id}/clone`, body),

  changeCounterparty: (id: string, body: ChangeCounterpartyRequest) =>
    api.post<DealDetailDto>(`/deals/${id}/change-counterparty`, body),

  // Assignment
  assignB1: (id: string, body: AssignB1Request) =>
    api.post<DealDetailDto>(`/deals/${id}/assign-b1`, body),

  reassignC1: (id: string, body: ReassignRequest) =>
    api.post<DealDetailDto>(`/deals/${id}/reassign-c1`, body),

  reassignB1: (id: string, body: ReassignRequest) =>
    api.post<DealDetailDto>(`/deals/${id}/reassign-b1`, body),

  reassignA1: (id: string, body: ReassignRequest) =>
    api.post<DealDetailDto>(`/deals/${id}/reassign-a1`, body),

  assignmentHistory: (id: string) =>
    api.get<DealAssignmentHistoryDto[]>(`/deals/${id}/assignment-history`),

  // Stage transitions (read-only feed)
  stageTransitions: (dealId: string) =>
    api.get<DealStageTransitionDto[]>(`/deals/${dealId}/stage-transitions`),

  // Broker Fee Authorization — the broker's compensation terms (monthly fee,
  // informational cash-at-closing, dormant sale fee) + verification state.
  brokerFeeAuthorization: (id: string) =>
    api.get<BrokerFeeAuthorizationDto>(`/deals/${id}/broker-fee-authorization`),

  updateBrokerFeeAuthorization: (id: string, body: UpdateBrokerFeeAuthorizationRequest) =>
    api.put<BrokerFeeAuthorizationDto>(`/deals/${id}/broker-fee-authorization`, body),

  confirmBrokerFeeAuthorization: (id: string) =>
    api.post<BrokerFeeAuthorizationDto>(`/deals/${id}/broker-fee-authorization/confirm`),

  // Buyer Consulting Fee Authorization — Phoenix's own two-leg consulting fee
  // (monthly + liquidity event) + verification state. Both percentages are
  // FRACTIONS on the wire; the form multiplies by 100 and divides on save.
  // Note confirm is a POST, like its broker sibling — the PUT is the edit.
  buyerConsultingFeeAuthorization: (id: string) =>
    api.get<BuyerConsultingFeeAuthorizationDto>(
      `/deals/${id}/buyer-consulting-fee-authorization`,
    ),

  updateBuyerConsultingFeeAuthorization: (
    id: string,
    body: UpdateBuyerConsultingFeeAuthorizationRequest,
  ) =>
    api.put<BuyerConsultingFeeAuthorizationDto>(
      `/deals/${id}/buyer-consulting-fee-authorization`,
      body,
    ),

  confirmBuyerConsultingFeeAuthorization: (id: string) =>
    api.post<BuyerConsultingFeeAuthorizationDto>(
      `/deals/${id}/buyer-consulting-fee-authorization/confirm`,
    ),
};

// ---------------------------------------------------------------------------
// Intake checklist
// ---------------------------------------------------------------------------

export const intakeChecklistApi = {
  get: (dealId: string) =>
    api.get<IntakeChecklistDto>(`/deals/${dealId}/intake-checklist`),
  // Bug-B fix — these three endpoints return a single IntakeChecklistItemDto
  // (or nothing, for delete), never the full IntakeChecklistDto. The prior
  // IntakeChecklistDto annotations were a type-level lie with no runtime
  // check behind them (api/client.ts casts `payload.data as T` unchecked),
  // which let onSuccess handlers in useDealDetail.ts silently overwrite the
  // whole cached checklist with a bare item — see that file for the fix.
  upsertItem: (dealId: string, itemId: string, body: UpsertChecklistItemRequest) =>
    api.patch<IntakeChecklistItemDto>(`/deals/${dealId}/intake-checklist/${itemId}`, body),
  addItem: (dealId: string, body: AddCustomChecklistItemRequest) =>
    api.post<IntakeChecklistItemDto>(`/deals/${dealId}/intake-checklist/items`, body),
  deleteItem: (dealId: string, itemId: string) =>
    api.delete<void>(`/deals/${dealId}/intake-checklist/items/${itemId}`),
  // TrackedItemRow (Deal Pipeline spec Appendix A).
  escalate: (dealId: string, itemId: string, escalationReason: string) =>
    api.post<IntakeChecklistItemDto>(
      `/deals/${dealId}/intake-checklist/${itemId}/escalate`,
      { escalationReason },
    ),
  updateNotes: (dealId: string, itemId: string, notes: string | null) =>
    api.patch<IntakeChecklistItemDto>(
      `/deals/${dealId}/intake-checklist/${itemId}/notes`,
      { notes },
    ),
};

// ---------------------------------------------------------------------------
// Pre-Approval document requests
// ---------------------------------------------------------------------------

export const documentRequestsApi = {
  list: (dealId: string) =>
    api.get<PreApprovalDocumentRequestDto[]>(`/deals/${dealId}/document-requests`),
  initialize: (dealId: string) =>
    api.post<PreApprovalDocumentRequestDto[]>(`/deals/${dealId}/document-requests/initialize`),
  create: (dealId: string, body: CreateDocumentRequestRequest) =>
    api.post<PreApprovalDocumentRequestDto>(`/deals/${dealId}/document-requests`, body),
  // Deal Pipeline spec §6.4 — single attestation submit for the popup.
  attest: (id: string, body: AttestDocumentRequestRequest) =>
    api.post<PreApprovalDocumentRequestDto>(`/document-requests/${id}/attest`, body),
  // TrackedItemRow (Deal Pipeline spec Appendix A).
  escalate: (id: string, escalationReason: string) =>
    api.post<PreApprovalDocumentRequestDto>(
      `/document-requests/${id}/escalate`,
      { escalationReason },
    ),
  updateNotes: (id: string, notes: string | null) =>
    api.patch<PreApprovalDocumentRequestDto>(
      `/document-requests/${id}/notes`,
      { notes },
    ),
};
